import React, { useEffect, useState } from "react";
import {
  Search,
  MessageCircle,
  FileText,
  ExternalLink,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import Layout from "../Layout/Layout";
import { API_BASE_URL } from "../../config";
import Loader from "../utils/Loader";
import { formatDateWithTime } from "../utils/formatDate";
import TrimText from "../utils/TrimText";
import GlobalDataTable from "../shared/GlobalDataTable.jsx";

const ITEMS_PER_PAGE = 100;

function WhatsAppMessagesTable() {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [notifications, setNotifications] = useState([]);

  const showNotification = (message, type = "success") => {
    const id = Date.now();
    const notification = { id, message, type };
    setNotifications((prev) => [...prev, notification]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setCurrentPage(1);
      setSearchQuery(searchInput.trim());
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      const token = window.__appSession.getToken();

      try {
        const params = new URLSearchParams({
          page: String(currentPage),
          limit: String(ITEMS_PER_PAGE),
        });

        if (searchQuery) {
          params.set("search", searchQuery);
        }

        const response = await fetch(`${API_BASE_URL}/api/whatsapp/messages?${params.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (Array.isArray(data.data)) {
          setMessages(data.data);
          setPagination({
            total: data.pagination?.total ?? data.data.length,
            totalRecords: data.pagination?.totalRecords ?? data.data.length,
            totalPages: data.pagination?.totalPages ?? 0,
            hasNextPage: data.pagination?.hasNextPage ?? false,
            hasPreviousPage: data.pagination?.hasPreviousPage ?? false,
          });
        }
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error("Error fetching messages:", error);
        setMessages([]);
        setPagination({
          total: 0,
          totalRecords: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        });
        showNotification("Failed to fetch messages.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [currentPage, searchQuery]);

  const pageCount = pagination.totalPages;
  const startItem = pagination.totalRecords === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = pagination.totalRecords === 0 ? 0 : startItem + messages.length - 1;

  const handlePageChange = (direction) => {
    if (direction === "prev" && pagination.hasPreviousPage) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else if (direction === "next" && pagination.hasNextPage) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  return (
    <Layout>
      <div>
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`px-3 py-2 rounded-lg shadow-lg text-xs font-medium transition-all duration-300 ${
                notification.type === "success"
                  ? "bg-green-500 text-white"
                  : notification.type === "error"
                    ? "bg-red-500 text-white"
                    : "bg-primary-500 text-white"
              }`}
            >
              {notification.message}
            </div>
          ))}
        </div>

        {/* Loading Overlay */}
        {/* {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`${theme.contentBg} p-4 rounded-lg shadow-xl`}>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current mx-auto"></div>
              <p className="mt-2 text-xs text-gray-600">Loading messages...</p>
            </div>
          </div>
        )} */}

        {isLoading && <Loader theme={theme} text="Loading messages..." />}

        <div className="">
          {/* Header */}

          <div className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-3 mb-3">
            <div
              className={`${theme.contentBg} ${theme.contentBorder} border border-gray-100 rounded-lg px-3 py-1`}
            >
              <div className="flex items-center gap-6 justify-between">
                <div className="flex items-center space-x-2">
                  <div
                    className={`p-2 ${theme.accentColor} bg-opacity-10 rounded-lg`}
                  >
                    <MessageCircle className={`h-4 w-4 ${theme.iconColor}`} />
                  </div>
                  <div>
                    <h1 className="text-base font-semibold text-gray-900">
                      WhatsApp Messages
                    </h1>
                    <p className="text-xs text-gray-500">
                      View all sent WhatsApp messages
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="text-center">
                  <div className="flex items-center space-x-1">
                    <MessageCircle className="h-3 w-3 text-primary-500" />
                    <span className="text-xs font-medium text-gray-900">
                      {pagination.total}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">Messages</p>
                </div>
              </div>
            </div>
            <div
              className={`${theme.contentBg} ${theme.contentBorder} border flex items-center w-full rounded-lg shadow-sm p-1`}
            >
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Search messages..."
                  className={`w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                />
              </div>
            </div>

            {/* Search */}
          </div>

          {/* Messages Table */}
          <GlobalDataTable
            columns={[
              {
                key: "index",
                header: "#",
                cellClassName: "text-xs font-medium text-gray-900",
                render: (_, i) => startItem + i,
              },
              {
                key: "createdAt",
                header: "Date & Time",
                cellClassName: "text-xs text-nowrap text-gray-700",
                render: (msg) => formatDateWithTime(msg.createdAt),
              },
              {
                key: "receiverMobileNo",
                header: "Mobile Number",
                cellClassName: "text-xs font-medium text-gray-900",
                render: (msg) => msg.receiverMobileNo,
              },
              {
                key: "message",
                header: "Message",
                cellClassName: "text-xs text-gray-700 max-w-xs",
                render: (msg) => (
                  <div title={msg.message}>
                    <TrimText text={msg.message} maxLen={60} />
                  </div>
                ),
              },
              {
                key: "filePathUrl",
                header: "Files",
                render: (msg) =>
                  msg.filePathUrl && msg.filePathUrl.length > 0 ? (
                    <div className="flex flex-col space-y-1">
                      {msg.filePathUrl.split(",").map((url, index) => (
                        <a
                          key={`${msg._id}-${index}`}
                          href={url.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-800 transition-colors"
                        >
                          <FileText className="h-3 w-3" />
                          <span>File {index + 1}</span>
                          <ExternalLink className="h-2 w-2" />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">No files</span>
                  ),
              },
            ]}
            rows={messages}
            rowKey={(msg) => msg._id}
            page={currentPage - 1}
            rowsPerPage={ITEMS_PER_PAGE}
            totalCount={pagination.total}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
            loading={isLoading}
            tableTitle={`Messages (${pagination.total})`}
            emptyText={searchQuery ? "No messages match your search" : "No messages found"}
            showRowsPerPage={false}
            canGoPrevious={pagination.hasPreviousPage && !isLoading}
            canGoNext={pagination.hasNextPage && !isLoading}
            onPreviousPage={() => handlePageChange("prev")}
            onNextPage={() => handlePageChange("next")}
            pageInfo={`Showing ${startItem} to ${endItem} of ${pagination.totalRecords} message records | Total recipients ${pagination.total} | Page ${currentPage} of ${Math.max(pageCount, 1)}`}
          />
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppMessagesTable;

// import React, { useEffect, useState } from "react";
// import { API_BASE_URL } from "../../config";
// import { motion } from "framer-motion";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import Layout from "../Layout/Layout";
// import ReactPaginate from "react-paginate";
// import { FallingLines } from "react-loader-spinner";

// function WhatsAppMessagesTable() {
//   const [messages, setMessages] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [isLoading, setLoading] = useState(false);
//   const [currentPage, setCurrentPage] = useState(0);
//   const itemsPerPage = 100;

//   useEffect(() => {
//     const fetchData = async () => {
//       setLoading(true);
//       const token = window.__appSession.getToken();
//       try {
//         const response = await fetch(`${API_BASE_URL}/api/whatsapp/messages`, {
//           method: "GET",
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//         });
//         if (!response.ok)
//           throw new Error(`HTTP error! status: ${response.status}`);
//         const data = await response.json();
//         if (Array.isArray(data.data)) {
//           const sortedMessages = data.data.sort(
//             (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
//           );
//           setMessages(sortedMessages);
//         }
//       } catch (error) {
//         console.error("Error fetching messages:", error);
//         toast.error("Failed to fetch messages.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchData();
//   }, []);

//   const formatDateTime = (dateString) => {
//     const date = new Date(dateString);
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const year = String(date.getFullYear()).slice(-2);
//     const hours = String(date.getHours()).padStart(2, "0");
//     const minutes = String(date.getMinutes()).padStart(2, "0");
//     const ampm = hours >= 12 ? "PM" : "AM";
//     const formattedHours = hours % 12 || 12;
//     return `${day}/${month}/${year}, ${formattedHours}:${minutes} ${ampm}`;
//   };

//   const filteredMessages = messages.filter((msg) =>
//     msg.receiverMobileNo.includes(searchTerm)
//   );

//   const offset = currentPage * itemsPerPage;
//   const currentPageItems = filteredMessages.slice(
//     offset,
//     offset + itemsPerPage
//   );
//   const pageCount = Math.ceil(filteredMessages.length / itemsPerPage);

//   const handlePageChange = ({ selected }) => {
//     setCurrentPage(selected);
//   };

//   return (
//     <Layout>
//       <motion.main
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         className="flex-grow w-full min-h-[calc(100vh-4rem)]"
//       >
//         <div className="bg-white p-4">
//           <h1 className="text-lg font-semibold text-gray-600 mb-2">
//             WhatsApp Messages{" "}
//             <span className="text-sm text-purple-700">
//               ({filteredMessages.length})
//             </span>
//           </h1>

//           <div className="mb-4">
//             <input
//               type="text"
//               value={searchTerm}
//               onChange={(e) => {
//                 setSearchTerm(e.target.value);
//                 setCurrentPage(0);
//               }}
//               className="w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-400 outline-none placeholder-gray-400 text-gray-900 bg-white text-xs"
//               placeholder="Search by receiver mobile number..."
//             />
//           </div>

//           <div
//             className="overflow-x-auto bg-white shadow-md border rounded-lg max-h-[40rem]"
//             style={{ scrollbarWidth: "thin" }}
//           >
//             <table className="min-w-full text-xs border-collapse text-gray-700">
//               <thead className="bg-gray-200 text-gray-700 sticky top-0 z-10">
//                 <tr>
//                   <th className="px-2 py-2 text-left font-medium">#</th>
//                   <th className="px-2 py-2 text-left font-medium">
//                     Date & Time
//                   </th>
//                   <th className="px-2 py-2 text-left font-medium">
//                     Receiver Mobile No
//                   </th>
//                   <th className="px-2 py-2 text-left font-medium">Message</th>
//                   <th className="px-2 py-2 text-left font-medium">
//                     File Path URLs
//                   </th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-200">
//                 {isLoading ? (
//                   <tr className="">
//                     <td colSpan={10}>
//                       <div className="flex justify-center">
//                         <FallingLines
//                           color="yellow"
//                           width="40"
//                           visible={true}
//                           ariaLabel="falling-circles-loading"
//                         />
//                       </div>
//                     </td>
//                   </tr>
//                 ) : currentPageItems.length === 0 ? (
//                   <tr>
//                     <td
//                       colSpan={10}
//                       className="text-center py-2 text-xs tracking-wide text-gray-700"
//                     >
//                       No messages available
//                     </td>
//                   </tr>
//                 ) : (
//                   currentPageItems.map((msg, i) => (
//                     <tr
//                       key={msg._id}
//                       className="hover:bg-gray-50 divide-x divide-gray-200 border-b border-gray-200"
//                     >
//                       <td className="px-2 py-1">{i + offset + 1}</td>
//                       <td className="px-2 py-1">
//                         {formatDateTime(msg.createdAt)}
//                       </td>
//                       <td className="px-2 py-1">{msg.receiverMobileNo}</td>
//                       <td className="px-2 py-1">{msg.message}</td>
//                       <td className="px-2 py-1">
//                         {msg.filePathUrl && msg.filePathUrl.length > 0 ? (
//                           msg.filePathUrl.split(",").map((url, index) => (
//                             <div key={index}>
//                               <a
//                                 href={url.trim()}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="text-[#25D366] hover:text-[#1a9d49] font-medium"
//                               >
//                                 File {index + 1}
//                               </a>
//                             </div>
//                           ))
//                         ) : (
//                           <span>-</span>
//                         )}
//                       </td>
//                     </tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>

//           <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mt-4">
//             <div className="text-sm text-gray-700">
//               Showing {filteredMessages.length === 0 ? 0 : offset + 1} to{" "}
//               {offset + currentPageItems.length} of {filteredMessages.length}{" "}
//               entries
//             </div>
//             <ReactPaginate
//               previousLabel={"Prev"}
//               nextLabel={"Next"}
//               breakLabel={"..."}
//               pageCount={pageCount}
//               marginPagesDisplayed={2}
//               pageRangeDisplayed={3}
//               onPageChange={handlePageChange}
//               containerClassName={"pagination flex space-x-2"}
//               pageClassName={
//                 "px-2 border border-gray-500 rounded-md cursor-pointer"
//               }
//               activeClassName={"bg-gray-300 text-black"}
//               previousClassName={
//                 "px-2 border border-gray-500 rounded-md cursor-pointer"
//               }
//               nextClassName={
//                 "px-2 border border-gray-500 rounded-md cursor-pointer"
//               }
//             />
//           </div>
//         </div>
//         <ToastContainer />
//       </motion.main>
//     </Layout>
//   );
// }

// export default WhatsAppMessagesTable;
